import { canAct as actorCanAct, selfAffects } from "./actorbattle";
import { isAttackable } from "./target";


/** What the reserved click does on this frame. */
export type PursuitAction =
  /** Forget the reservation: the target is gone, or the walk ended short. */
  | "drop"
  /** In reach of something attackable: spend the click as a swing. */
  | "swing"
  /** In talk range of the NPC: spend the click as `ClickNpc`. */
  | "talk"
  /** Still approaching: re-aim the walk at where the target is now. */
  | "follow";

export interface PursuitTarget {
  readonly kind: number;
  readonly hp: number;
  readonly maxHp: number;
  readonly xMm: number;
  readonly yMm: number;
}

export interface PursuitInput {
  /** The reserved target as the world has it now, or null once it is gone. */
  readonly quarry: PursuitTarget | null;
  /** A click on an NPC (talk), as opposed to one on something to fight. */
  readonly talk: boolean;
  readonly selfXMm: number;
  readonly selfYMm: number;
  /** `WalkTo.walking` -- false on the frame the approach arrives. */
  readonly walking: boolean;
  readonly reachMm: number;
  readonly arriveMm: number;
  /** `__CanAttack()` -- alive, and not behind a private shop board. */
  readonly canAct: boolean;
  /** `isLoot(quarry)`: a dropped item is walked to, never swung at. */
  readonly quarryIsLoot: boolean;
}

/** `CanAct` for the client's own body (`ActorInstanceBattle.cpp:18-35`), its
 *  states read by `selfAffects`. */
export function canAct(dead: boolean, flags: number): boolean {
  return actorCanAct(selfAffects(dead, flags));
}

export function resolvePursuit(input: PursuitInput): PursuitAction {
  const quarry = input.quarry;
  if (quarry === null) return "drop";
  const dx = quarry.xMm - input.selfXMm;
  const dy = quarry.yMm - input.selfYMm;
  const awayMmSq = dx * dx + dy * dy;
  const spendMm = Math.min(input.arriveMm, input.reachMm);
  if (awayMmSq <= spendMm * spendMm) {
    // Arrival, answered ahead of `walking` -- see the module docstring. Both
    // arms sit here, above the drop, for the same reason: the frame the walk
    // arrives on is the frame `walking` turns false.
    if (input.talk) return "talk";
    // `isAttackable` is the whole of the original's `IsAttackableInstance`
    // (kind and dead flag, never a health), so loot, NPCs and corpses are all
    // refused by one rule -- `input.quarryIsLoot` stays only because a click
    // on a drop is a *pickup* the caller still has to tell apart from a talk.
    if (!input.quarryIsLoot && isAttackable(quarry) && input.canAct) return "swing";
  }
  if (!input.walking) return "drop";
  return "follow";
}
